<template>
  <v-card class="pa-3">
    <v-toolbar flat dense>
      <v-toolbar-title class="text-h5 font-weight-bold">SMS</v-toolbar-title>
      <v-spacer />
      <v-btn
        v-if="superUser"
        color="secondary"
        class="text-capitalize px-4 mx-2"
        depressed
        @click="openBulkDialog"
      >
        {{ translations.enableDisableAll[lang] }}
      </v-btn>
      <v-col cols="12" sm="4" class="py-0 pr-0">
        <b-search-field v-model="search"></b-search-field>
      </v-col>
    </v-toolbar>

    <b-data-table :headers="headers" :items="filteredCharges" :loading="loading">
      <template v-slot:item.country="{ item }">
        <span> {{ item.country?.name }} ({{ item.country?.code }})</span>
      </template>

      <template v-slot:item.enabled="{ item }">
        <div class="switch-cell">
          <v-switch
            :input-value="item.enabled"
            :disabled="!superUser"
            @change="toggleEnabled(item, $event)"
            inset
            hide-details
          ></v-switch>
        </div>
      </template>

      <template v-slot:item.cost="{ item }">
        <div
          v-if="editingItem && editingItem.country_id === item.country_id"
          class="d-flex align-center"
        >
          <v-text-field
            v-model="editingCost"
            type="number"
            dense
            :error-messages="costError"
            style="max-width: 140px"
          ></v-text-field>
          <v-btn icon small color="primary" @click="saveCost(item)">
            <v-icon>mdi-check</v-icon>
          </v-btn>
          <v-btn icon small color="grey" @click="cancelEdit">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <div v-else class="d-flex align-center">
          <div>{{ formatCost(item.cost) }}</div>
          <v-icon
            small
            class="ml-2"
            v-if="superUser"
            @click="startEdit(item)"
            style="cursor: pointer"
          >
            mdi-pencil-outline
          </v-icon>
        </div>
      </template>
    </b-data-table>

    <v-dialog v-model="bulkDialog" max-width="500">
      <b-card
        :title="translations.enableAll[lang]"
        @submit="confirmBulk"
        @cancel="bulkDialog = false"
      >
        <v-container>
          <v-row>
            <v-col cols="12">
              <div class="text-body-2 grey--text mb-3">
                {{ translations.enableAllDescription[lang] }}
              </div>
              <v-radio-group v-model="bulkEnable" class="mt-3 pt-0" hide-details>
                <v-radio
                  color="secondary"
                  :label="translations.enableAll[lang]"
                  :value="true"
                ></v-radio>
                <v-radio
                  color="secondary"
                  :label="translations.disableAll[lang]"
                  :value="false"
                ></v-radio>
              </v-radio-group>
            </v-col>
          </v-row>
        </v-container>
      </b-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import translations from '@/utils/translations/general/settings/billingDashboard';
import chargesValidator from '@/utils/validators/general/settings/billingDashboard/charges';

export default {
  name: 'BillingChargesSms',
  mixins: [translations, chargesValidator],

  data() {
    return {
      search: '',
      editingItem: null,
      editingCost: null,
      costError: '',
      bulkDialog: false,
      bulkEnable: true,
    };
  },

  computed: {
    ...mapState(['superUser']),
    ...mapState('general/settings/billingDashboard/charges', ['smsCharges', 'loading']),

    lang() {
      return this.$route.params.lang;
    },

    filteredCharges() {
      if (!Array.isArray(this.smsCharges)) return [];
      if (!this.search) return this.smsCharges;
      const q = this.search.toLowerCase();
      return this.smsCharges.filter(
        (item) =>
          item.country?.name?.toLowerCase().includes(q) ||
          item.country?.code?.toLowerCase().includes(q) ||
          String(item.cost).includes(q) ||
          String(item.enabled).includes(q)
      );
    },

    headers() {
      return [
        { text: this.translations.smsTable_country[this.lang], value: 'country' },
        { text: this.translations.smsTable_cost[this.lang], value: 'cost' },
        { text: this.translations.smsTable_enabled[this.lang], value: 'enabled' },
      ];
    },
  },

  methods: {
    ...mapActions('general/settings/billingDashboard/charges', [
      'getSmsCharges',
      'updateSmsCharge',
      'bulkUpdateSmsEnabled',
    ]),

    formatCost(val) {
      if (val === null || val === undefined) return '-';
      return val;
    },

    startEdit(item) {
      this.editingItem = { ...item };
      this.editingCost = item.cost;
      this.costError = '';
    },

    cancelEdit() {
      this.editingItem = null;
      this.editingCost = null;
      this.costError = '';
    },

    async saveCost(item) {
      const cost = Number(this.editingCost);
      if (!this.validateCost(this.editingCost)) {
        this.costError = this.error.cost;
        return;
      }
      const originalCost = item.cost;
      try {
        const body = { cost };
        const res = await this.updateSmsCharge({
          country_id: item.country_id,
          body,
        });
        if (res && !res.error) {
          this.$store.commit('setNotification', {
            show: true,
            type: 'success',
            text: this.translations.invoiceItemUpdated[this.lang],
          });
        } else {
          item.cost = originalCost;
          this.$store.commit('setNotification', {
            show: true,
            type: 'error',
            text: res?.message || 'Failed to update cost',
          });
        }
      } catch (e) {
        item.cost = originalCost;
        this.$store.commit('setNotification', {
          show: true,
          type: 'error',
          text: 'Failed to update cost',
        });
      } finally {
        this.editingItem = null;
        this.editingCost = null;
        this.costError = '';
      }
    },

    async toggleEnabled(item, newVal) {
      const original = item.enabled;
      item.enabled = newVal;
      try {
        const body = { enabled: newVal };
        const res = await this.updateSmsCharge({ country_id: item.country_id, body });
        if (res && !res.error) {
          this.$store.commit('setNotification', {
            show: true,
            type: 'success',
            text:
              this.translations.success[this.lang] +
              (newVal ? this.translations.active_true[this.lang] : this.translations.active_false[this.lang]),
          });
        } else {
          item.enabled = original;
          this.$store.commit('setNotification', {
            show: true,
            type: 'error',
            text: res?.message || 'Failed to update',
          });
        }
      } catch (e) {
        item.enabled = original;
        this.$store.commit('setNotification', {
          show: true,
          type: 'error',
          text: 'Failed to update',
        });
      }
    },

    openBulkDialog() {
      this.bulkDialog = true;
    },

    async confirmBulk() {
      const res = await this.bulkUpdateSmsEnabled({ enabled: !!this.bulkEnable });
      if (res && !res.error) {
        this.$store.commit('setNotification', {
          show: true,
          type: 'success',
          text:
            this.translations.success[this.lang] +
            (this.bulkEnable ? this.translations.active_true[this.lang] : this.translations.active_false[this.lang]),
        });
      } else {
        this.$store.commit('setNotification', {
          show: true,
          type: 'error',
          text: res?.message || 'Failed to update',
        });
      }
      this.bulkDialog = false;
      this.getSmsCharges();
    },
  },

  watch: {
    '$store.state.storeId': {
      handler(val) {
        if (val) this.getSmsCharges();
      },
      immediate: true,
    },
  },
};
</script>

<style scoped>
.pointer {
  cursor: pointer;
}
.switch-cell {
  display: inline-flex;
}
.switch-cell ::v-deep .v-input--selection-controls {
  margin-top: 0;
  padding-top: 0;
}
.switch-cell ::v-deep .v-input__slot {
  margin-bottom: 0;
}
</style>
